import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useApp, fmt, fmtShort } from "../../context/AppContext";

const ASSET_FIELDS = [
  { key: "isa", label: "Stocks & Shares ISA", color: "text-emerald-400" },
  { key: "pension", label: "Pension (SIPP/Workplace)", color: "text-emerald-300" },
  { key: "house", label: "Property Value", color: "text-teal-400" },
  { key: "cash", label: "Cash Savings", color: "text-sky-400" },
  { key: "crypto", label: "Crypto", color: "text-violet-400" },
  { key: "otherAsset", label: "Other Assets", color: "text-slate-300" },
];

const LIABILITY_FIELDS = [
  { key: "mortgage", label: "Mortgage", color: "text-rose-400" },
  { key: "studentLoan", label: "Student Loan", color: "text-rose-300" },
  { key: "creditCard", label: "Credit Card Debt", color: "text-red-400" },
  { key: "otherLiability", label: "Other Liabilities", color: "text-slate-400" },
];

const BarTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 shadow-xl text-sm">
        {payload.map((p) => (
          <div key={p.name} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
            <span className="text-slate-300">{p.name}:</span>
            <span className="text-white font-bold tabular-nums">{fmtShort(Math.abs(p.value))}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

function EditableField({ value, onChange, label, color }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 group">
      <span className={`text-sm ${color}`}>{label}</span>
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">£</span>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="w-28 bg-slate-800/60 border border-transparent focus:border-slate-600 group-hover:border-slate-700 rounded-lg pl-6 pr-2 py-1 text-white text-sm font-semibold tabular-nums outline-none text-right transition-colors"
        />
      </div>
    </div>
  );
}

export default function NetWorth() {
  const { state, dispatch, totalAssets, totalLiabilities, netWorth } = useApp();

  const chartData = [
    { name: "You", Assets: totalAssets, Liabilities: -totalLiabilities },
  ];

  const assetBreakdown = ASSET_FIELDS.map((f) => ({ ...f, amount: state.assets[f.key] || 0 }));
  const liabBreakdown = LIABILITY_FIELDS.map((f) => ({ ...f, amount: state.liabilities[f.key] || 0 }));

  return (
    <div className="space-y-6">
      {/* Net Worth Hero */}
      <div data-testid="net-worth-hero" className="relative bg-slate-900/40 border border-white/5 rounded-xl p-6 text-center overflow-hidden">
        <div className="absolute inset-0" style={{ background: netWorth >= 0 ? "radial-gradient(circle at 50% 120%, rgba(16,185,129,0.07), transparent)" : "radial-gradient(circle at 50% 120%, rgba(244,63,94,0.07), transparent)" }} />
        <p className="text-slate-500 text-sm mb-2 relative z-10">Total Net Worth</p>
        <p data-testid="net-worth-value" className={`font-outfit font-bold text-5xl tabular-nums tracking-tight relative z-10 ${netWorth >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
          {fmt(netWorth)}
        </p>
        <div className="flex items-center justify-center gap-2 mt-2 relative z-10">
          {netWorth >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <TrendingDown className="w-4 h-4 text-rose-400" />}
          <span className="text-slate-400 text-sm">{fmt(totalAssets)} assets &minus; {fmt(totalLiabilities)} liabilities</span>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assets */}
        <div data-testid="assets-column" className="bg-slate-900/40 border border-emerald-500/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-white">Assets</h2>
            <span className="font-outfit font-bold text-emerald-400 tabular-nums">{fmt(totalAssets)}</span>
          </div>
          {ASSET_FIELDS.map((f) => (
            <EditableField
              key={f.key}
              label={f.label}
              color={f.color}
              value={state.assets[f.key] || 0}
              onChange={(v) => dispatch({ type: "UPDATE_ASSETS", payload: { [f.key]: v } })}
            />
          ))}
          <div className="mt-3 pt-3 border-t border-emerald-500/10 flex justify-between">
            <span className="text-slate-500 text-sm">Total Assets</span>
            <span className="font-outfit font-bold text-emerald-400 tabular-nums">{fmt(totalAssets)}</span>
          </div>
        </div>

        {/* Chart */}
        <div data-testid="net-worth-chart" className="bg-slate-900/40 border border-white/5 rounded-xl p-5 flex flex-col">
          <h2 className="font-outfit font-semibold text-white mb-4">Balance Sheet</h2>
          <div className="flex-1 min-h-[220px]">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} barCategoryGap="40%">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => fmtShort(Math.abs(v))} tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} width={55} />
                <Tooltip content={<BarTooltip />} />
                <Bar dataKey="Assets" fill="#10b981" radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="Assets" position="top" formatter={fmtShort} style={{ fill: "#10b981", fontSize: 11, fontWeight: 700 }} />
                </Bar>
                <Bar dataKey="Liabilities" fill="#f43f5e" radius={[0, 0, 6, 6]}>
                  <LabelList dataKey="Liabilities" position="bottom" formatter={(v) => fmtShort(Math.abs(v))} style={{ fill: "#f43f5e", fontSize: 11, fontWeight: 700 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-3 border-t border-white/5 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-1.5 text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Assets</span>
              <span className="text-emerald-400 font-semibold tabular-nums">{fmt(totalAssets)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-1.5 text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />Liabilities</span>
              <span className="text-rose-400 font-semibold tabular-nums">{fmt(totalLiabilities)}</span>
            </div>
          </div>
        </div>

        {/* Liabilities */}
        <div data-testid="liabilities-column" className="bg-slate-900/40 border border-rose-500/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-white">Liabilities</h2>
            <span className="font-outfit font-bold text-rose-400 tabular-nums">{fmt(totalLiabilities)}</span>
          </div>
          {LIABILITY_FIELDS.map((f) => (
            <EditableField
              key={f.key}
              label={f.label}
              color={f.color}
              value={state.liabilities[f.key] || 0}
              onChange={(v) => dispatch({ type: "UPDATE_LIABILITIES", payload: { [f.key]: v } })}
            />
          ))}
          <div className="mt-3 pt-3 border-t border-rose-500/10 flex justify-between">
            <span className="text-slate-500 text-sm">Total Liabilities</span>
            <span className="font-outfit font-bold text-rose-400 tabular-nums">{fmt(totalLiabilities)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
